type StringOrNumberKeys<T> = {
    [K in keyof T]: NonNullable<T[K]> extends string | number ? K : never;
}[keyof T];

export function sortBy<T>(key: StringOrNumberKeys<T>): (a: T, b: T) => number {
    return (a: T, b: T) => {
        const aVal = a[key];
        const bVal = b[key];

        if (typeof aVal === "string" && typeof bVal === "string") {
            return aVal.localeCompare(bVal);
        }

        if (typeof aVal === "number" && typeof bVal === "number") {
            return aVal - bVal;
        }

        return 0;
    };
}

export function sortItemsBy(items: string[]): string[];
export function sortItemsBy(items: number[]): number[];
export function sortItemsBy<T>(items: T[], key: StringOrNumberKeys<T>): T[];
export function sortItemsBy<T>(items: T[], key?: StringOrNumberKeys<T>): T[] {
    if (key !== undefined) {
        return items.toSorted(sortBy(key));
    }

    return items.toSorted((a, b) => {
        const aVal = a as unknown;
        const bVal = b as unknown;

        if (typeof aVal === "string" && typeof bVal === "string") {
            return aVal.localeCompare(bVal);
        }

        if (typeof aVal === "number" && typeof bVal === "number") {
            return aVal - bVal;
        }

        return 0;
    });
}

export function sortByCustomOrder<T extends string | number>(items: T[], order: T[]): T[];
export function sortByCustomOrder<T>(items: T[], key: StringOrNumberKeys<T>, order: T[StringOrNumberKeys<T>][]): T[];
export function sortByCustomOrder<T>(
    items: T[],
    keyOrOrder: StringOrNumberKeys<T> | T[],
    order?: T[StringOrNumberKeys<T>][],
): T[] {
    return items.toSorted((a, b) => {
        if (Array.isArray(keyOrOrder)) {
            const primitiveOrder = keyOrOrder as T[];
            return compareByCustomOrder(a, b, primitiveOrder);
        }

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return compareByCustomOrder(a[keyOrOrder], b[keyOrOrder], order!);
    });
}

export function compareByCustomOrder<T>(a: T, b: T, order: T[]): number {
    const aPos = order.indexOf(a);
    const bPos = order.indexOf(b);

    return (aPos === -1 ? order.length : aPos) - (bPos === -1 ? order.length : bPos);
}
